import PropTypes from 'prop-types'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { BannerActionLarge } from '@loadsmart/loadsmart-ui'

import { PreferredLaneRateModal } from 'legacy/containers/PreferredLanes/PreferredLaneRateModal/PreferredLaneRateModal'
import { LoadBoardContext, LOAD_BOARD_ACTIONS } from 'legacy/contexts/loadBoard'
import { analytics } from 'legacy/utils/analytics'

import { createPreferredLane } from 'preferred-lanes/domain/PreferredLane'
import { usePreferredLane } from 'preferred-lanes/ui/hooks/usePreferredLane'

import { getBidDescription } from '../utils/getBidDescription'
import { Notification } from './Notification'

function BidOfferAcceptedNotification({ bid }) {
  const {
    load: { uuid: loadUUID, ref_number, stops },
    price,
  } = bid
  const {
    preferredLaneId,
    isLoading,
    savePreferredLane,
    removePreferredLane,
    checkIfPreferredLaneExists,
  } = usePreferredLane()
  const [rateModalOpen, setRateModalOpen] = useState(false)
  const lane = useMemo(() => createPreferredLane(bid.load.stops), [bid.load])
  const [, dispatch] = useContext(LoadBoardContext)

  const bidDescription = getBidDescription(stops, price)

  const hideBanner = useCallback(() => {
    dispatch({ type: LOAD_BOARD_ACTIONS.REMOVE_BID, payload: loadUUID })
  }, [loadUUID, dispatch])

  const handleDismiss = useCallback(() => {
    analytics.track('Bid Offer Accepted Banner / Dismiss')
    hideBanner()
  }, [hideBanner])

  const handleSavePreferredLane = useCallback(async () => {
    if (ref_number && !isLoading) {
      try {
        await savePreferredLane(lane)
        analytics.track(`Bid Offer Accepted Banner / Preferred Lane / Add`)
        setRateModalOpen(true)
        toast.success('Preferred lane saved successfully')
      } catch (error) {
        toast.error(error.message)
      }
    }
  }, [ref_number, isLoading, savePreferredLane, lane])

  const handleRemovePreferredLane = useCallback(async () => {
    try {
      await removePreferredLane(lane)
      analytics.track(`Bid Offer Accepted Banner / Preferred Lane / Remove`)
      toast.success('Preferred lane removed')
    } catch (error) {
      toast.error(error.message)
    }
  }, [removePreferredLane, lane])

  const handlePrimaryActionClick = useCallback(() => {
    if (isLoading) return

    if (preferredLaneId) {
      handleRemovePreferredLane()
    } else {
      handleSavePreferredLane()
    }
  }, [isLoading, preferredLaneId, handleRemovePreferredLane, handleSavePreferredLane])

  const handleClosePreferredLaneRateModal = useCallback(() => {
    setRateModalOpen(false)
  }, [])

  useEffect(() => {
    analytics.track('Bid Offer Accepted Banner / View')
    checkIfPreferredLaneExists(lane)
  }, [checkIfPreferredLaneExists, lane])

  return (
    <Notification>
      {rateModalOpen && (
        <PreferredLaneRateModal
          preferredLaneId={preferredLaneId}
          lane={lane}
          open={rateModalOpen}
          onClose={handleClosePreferredLaneRateModal}
          page={'Load Board / Bid'}
        />
      )}
      <BannerActionLarge
        title={`The Load ${bidDescription} is yours!`}
        variant="success"
        dismissible={true}
        onClose={handleDismiss}
        action={preferredLaneId ? 'Saved preferred lane' : 'Save preferred lane'}
        onActionButtonClick={handlePrimaryActionClick}
      />
    </Notification>
  )
}

BidOfferAcceptedNotification.propTypes = {
  bid: PropTypes.shape({}).isRequired,
}

export { BidOfferAcceptedNotification }
